import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { labService, runService } from '../services';
import { Lab } from '../types';
import { LoadingSpinner, Card, Badge, Button, Alert } from '../components/ui';
import ReactMarkdown from 'react-markdown';
import { EnhancedInstructions } from '../components/EnhancedInstructions';

export function LabDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [lab, setLab] = useState<Lab | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingLab, setStartingLab] = useState(false);
  // Spaces support state
  const [spacesCreds, setSpacesCreds] = useState<any[]>([]);
  const [selectedCredId, setSelectedCredId] = useState<number | null>(null);
  const [spacesBucket, setSpacesBucket] = useState('');
  const [spacesPrefix, setSpacesPrefix] = useState('');

  useEffect(() => {
    if (slug) {
      loadLabDetails(slug);
    }
  }, [slug]);

  // Load Spaces credentials when viewing the Spaces lab
  useEffect(() => {
    if (!slug || slug !== 'spaces-backup-automation') return;
    (async () => {
      try {
        const { userService } = await import('../services');
        const list = await userService.getSpacesCredentials();
        setSpacesCreds(list);
      } catch (e) {
        console.warn('Failed to load Spaces credentials', e);
      }
    })();
  }, [slug]);

  const loadLabDetails = async (labSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      const labDetails = await labService.getLab(labSlug);
      setLab(labDetails);
    } catch (err: any) {
      console.error('Failed to load lab details:', err);
      if (err.status === 404) {
        setError('Lab not found. The lab you are looking for does not exist.');
      } else {
        setError('Failed to load lab details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'bg-green-500';
      case 'intermediate':
        return 'bg-yellow-500';
      case 'advanced':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleStartLab = async () => {
    if (!lab) return;

    try {
      setStartingLab(true);
      setError(null);

      // Build run payload (with optional Spaces config)
      const payload: any = { lab_id: lab.slug };
      if (lab.slug === 'spaces-backup-automation') {
        if (!selectedCredId || !spacesBucket) {
          setError('Please select a Spaces credential and enter a bucket name.');
          setStartingLab(false);
          return;
        }
        payload.spaces_credential_id = selectedCredId;
        payload.spaces_bucket = spacesBucket;
        if (spacesPrefix) payload.spaces_prefix = spacesPrefix;
      }

      // Create the run
      const run = await runService.createRun(payload);
      
      // Navigate to run monitoring page
      navigate(`/runs/${run.id}`);
      
    } catch (err: any) {
      console.error('Failed to start lab:', err);
      
      // Handle specific error cases
      let errorMessage = 'Failed to start lab. Please try again.';
      
      if (err.status === 429 || (err.message && err.message.includes('Maximum'))) {
        errorMessage = 'Maximum 2 active labs per user. Please destroy existing labs before starting new ones.';
      } else if (err.status === 404) {
        errorMessage = 'Lab not found. Please refresh the page and try again.';
      } else if (err.status === 400 && err.message && err.message.includes('SSH key required')) {
        errorMessage = 'SSH key required. Please add an SSH key to your account in Settings before starting labs.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setStartingLab(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-white bg-gray-900 min-h-screen">
        <div className="flex items-center justify-center mt-20">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error && !lab) {
    return (
      <div className="p-8 text-white bg-gray-900 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
          <Button onClick={() => navigate('/labs')} className="mt-4">
            Back to Labs Catalog
          </Button>
        </div>
      </div>
    );
  }

  if (!lab) {
    return null;
  }

  return (
    <div className="p-8 text-white bg-gray-900 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header with navigation */}
        <div className="mb-6">
          <Link to="/labs" className="text-blue-400 hover:text-blue-300 mb-4 inline-block">
            ← Back to Labs Catalog
          </Link>
        </div>

        {/* Lab Title and Metadata */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-3">{lab.title}</h1>
              <div className="flex items-center gap-4">
                <Badge 
                  className={`${getDifficultyColor(lab.difficulty)} text-white px-3 py-1`}
                >
                  {lab.difficulty}
                </Badge>
                <span className="text-gray-400">
                  <span className="text-gray-500">Duration:</span> {lab.estimated_minutes} minutes
                </span>
                <span className="text-gray-400">
                  <span className="text-gray-500">OS:</span> {lab.os || 'Ubuntu'}
                </span>
              </div>
            </div>
          </div>

          {/* Tags */}
          {lab.tags && lab.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {lab.tags.map((tag) => (
                <Badge key={tag} variant="default" size="sm">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Summary */}
          {lab.summary && (
            <p className="text-gray-300 text-lg leading-relaxed">
              {lab.summary}
            </p>
          )}
        </div>

        {/* Error Alert if any */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Instructions */}
          <div className="lg:col-span-2">
            {(slug === 'linux-file-permissions-users' || slug === 'broken-web-server-rescue' || slug === 'service-startup-failures' || slug === 'django-application-disaster' || slug === 'nextjs-fullstack' || slug === 'wordpress-basics' || slug === 'wordpress-database-connection-failure' || slug === 'memory-management-mayhem' || slug === 'react-node-stack-breakdown' || slug === 'database-replication' || slug === 'k3s-mini-cluster' || slug === 'storage-migration-scaling' || slug === 'backup-disaster-recovery' || slug === 'basic-shell-scripting' || slug === 'nginx-setup' || slug === 'spaces-backup-automation') ? (
              <EnhancedInstructions 
                labSlug={slug}
                instructions={lab.manifest?.instructions}
              />
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <div className="p-6">
                  <h2 className="text-2xl font-semibold mb-4">Instructions</h2>
                  <div className="prose prose-invert max-w-none">
                    {lab.manifest?.instructions ? (
                      <ReactMarkdown
                        components={{
                          h1: ({children}: any) => <h1 className="text-3xl font-bold mb-4 text-white">{children}</h1>,
                          h2: ({children}: any) => <h2 className="text-2xl font-semibold mb-3 mt-6 text-white">{children}</h2>,
                          h3: ({children}: any) => <h3 className="text-xl font-medium mb-2 mt-4 text-white">{children}</h3>,
                          p: ({children}: any) => <p className="mb-4 text-gray-300 leading-relaxed">{children}</p>,
                          ul: ({children}: any) => <ul className="list-disc list-inside mb-4 text-gray-300">{children}</ul>,
                          ol: ({children}: any) => <ol className="list-decimal list-inside mb-4 text-gray-300">{children}</ol>,
                          li: ({children}: any) => <li className="mb-1">{children}</li>,
                          code: ({inline, children}: any) => 
                            inline ? (
                              <code className="bg-gray-700 px-1 py-0.5 rounded text-blue-300">{children}</code>
                            ) : (
                              <code className="block bg-gray-900 p-3 rounded overflow-x-auto text-blue-300">{children}</code>
                            ),
                          pre: ({children}: any) => <pre className="bg-gray-900 p-3 rounded overflow-x-auto mb-4">{children}</pre>,
                          blockquote: ({children}: any) => <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-400">{children}</blockquote>,
                        }}
                      >
                        {lab.manifest.instructions}
                      </ReactMarkdown>
                    ) : (
                      <p className="text-gray-400">No instructions available for this lab.</p>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {slug === 'spaces-backup-automation' && (
              <div className="mt-6 bg-gray-800 border border-gray-700 rounded p-4">
                <h3 className="text-white font-semibold mb-2">Spaces Configuration</h3>
                {spacesCreds.length === 0 ? (
                  <p className="text-gray-400 text-sm">
                    No Spaces credentials found. Add one on the <a href="/spaces-credentials" className="text-blue-400 underline">Spaces</a> page.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Credential</label>
                      <select
                        className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                        value={selectedCredId ?? ''}
                        onChange={e => setSelectedCredId(e.target.value ? parseInt(e.target.value, 10) : null)}
                        data-testid="spaces-credential-select"
                      >
                        <option value="">Select a credential…</option>
                        {spacesCreds.map((c: any) => (
                          <option key={c.id} value={c.id}>{c.name} ({c.access_key_id})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Bucket</label>
                      <input
                        className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                        placeholder="my-unique-bucket"
                        value={spacesBucket}
                        onChange={e => setSpacesBucket(e.target.value)}
                        data-testid="spaces-bucket-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Prefix (optional)</label>
                      <input
                        className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                        placeholder={"backups/${HOST}/${DATE}/"}
                        value={spacesPrefix}
                        onChange={e => setSpacesPrefix(e.target.value)}
                        data-testid="spaces-prefix-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Lab Details and Actions */}
          <div className="lg:col-span-1">
            {/* Start Lab Button - Simplified */}
            <div className="sticky top-8">
              <Button 
                onClick={handleStartLab}
                disabled={startingLab}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                size="lg"
                data-testid="start-lab-button"
              >
                {startingLab ? (
                  <div className="flex items-center justify-center space-x-2">
                    <LoadingSpinner size="sm" />
                    <span>Starting Lab...</span>
                  </div>
                ) : (
                  'Start Lab'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
